import React, { useMemo } from 'react';
import { ImagePreview } from '@common/components/ImagePreview';
import type { Item as EventItem } from '@common/components/ItemListingRows/Event/Event';
import type { Item as VoucherItem } from '@common/components/ItemListingRows/Voucher/Voucher';
import { HelperText, Label, Text, Title1 } from '@common/components/Typography';
import { StickyBarContent, StickyBarWrapper } from '@common/components/StickyBar';
import { Button } from '@common/components/Button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';
import { isVoucher } from '@common-types/Guards';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { FormRowWrapper, FormWrapper, HeaderWrapper } from '../Form/Form.styles';
import {
  maximumMessageLength,
  maximumRecipientLength,
  maximumSalutationLength,
} from '../constants';
import {
  Fields,
  FieldsWrapper,
  Image,
  MessageInputs,
  MessageWrapper,
  NameInput,
  NameInputs,
  SalutationInput,
} from '../Form/InputFields/InputFields.styles';
import { nextFocus, remainingCharacters } from '../Form/InputFields/services';
import { Preview } from '../Form/Preview/Preview';
import { ChangeDate, SelectDateWrapper } from '../Form/SelectDate/SelectDate.styles';
import { BasketCTAButton, BasketToolbar } from '../Form/AddToBasket/AddToBasket.styles';
import { PersonalizationSchema } from '../schema';
import { PersonalizationFormValues } from '../types';
import { DatePicker } from './DatePicker';
import { Field } from './Field';
import { MessageLine } from './MessageLine';
import { Personalization } from 'shared-types';

type PersonalisationFormProps = {
  item: VoucherItem | EventItem;
  previewOptions: {
    propertyId: number;
    hasDefaultEVoucher: boolean;
    hasDefaultOverprint: boolean;
    hasDefaultETicket: boolean;
    hasDefaultTicketOverprint: boolean;
  };
  initialValues?: PersonalizationFormValues;
  handleSubmission: (id: number, data: PersonalizationFormValues['personalization']) => void;
};

export function PersonalisationForm({
  item,
  previewOptions,
  initialValues,
  handleSubmission,
}: PersonalisationFormProps) {
  const { t } = useTranslations();
  const data = isVoucher(item) ? item.voucher : item.event;
  const { id, imageGallery: images } = data;
  const option = isVoucher(item) ? item.voucher.voucherOption : item.event.eventOption;

  const name = useMemo(() => {
    return item?.detailsBag?.name ?? data.name;
  }, [data?.name, item]);

  const {
    propertyId,
    hasDefaultEVoucher,
    hasDefaultOverprint,
    hasDefaultETicket,
    hasDefaultTicketOverprint,
  } = previewOptions;

  const form = useForm<PersonalizationFormValues>({
    resolver: zodResolver(PersonalizationSchema),
    defaultValues: initialValues,
  });

  const { control, watch } = form;

  const values = watch('personalization');

  const { fields } = useFieldArray({
    control,
    name: 'personalization',
  });

  const onSubmit: SubmitHandler<PersonalizationFormValues> = (formValues) => {
    handleSubmission(item.id, formValues.personalization);
  };

  return (
    <FormProvider {...form}>
      <form data-cy="personalisation-form-form" onSubmit={form.handleSubmit(onSubmit)}>
        <FormWrapper>
          {fields.map((field, index) => (
            <div key={field.id}>
              <HeaderWrapper>
                <Title1 data-cy="personalsiation-form-title" tag="h2">
                  {name}
                </Title1>
              </HeaderWrapper>
              <FormRowWrapper>
                <FieldsWrapper key={field.id}>
                  <Fields>
                    <NameInputs>
                      <SalutationInput data-cy="salutation-input">
                        <Field
                          control={control}
                          name={`personalization.${index}.salutation`}
                          label={t('personalization.salutation')}
                          maxLength={parseInt(maximumSalutationLength)}
                          data-testid="salutation"
                        />
                      </SalutationInput>
                      <NameInput data-cy="name-input">
                        <Field
                          control={control}
                          name={`personalization.${index}.recipient`}
                          label={t('personalization.recipient')}
                          maxLength={parseInt(maximumRecipientLength)}
                          data-testid="recipient"
                        />
                      </NameInput>
                    </NameInputs>

                    <MessageWrapper data-cy="message-wrapper">
                      <Label htmlFor={`personalization.${index}.messageLineOne`}>
                        {t('personalization.messageLine')}
                      </Label>
                      <MessageInputs>
                        <MessageLine
                          data-cy="message-line-1"
                          control={control}
                          name={`personalization.${index}.messageLineOne`}
                          maxLength={parseInt(maximumMessageLength)}
                          onKeyDown={nextFocus}
                          data-testid="message1"
                        />
                        <MessageLine
                          data-cy="message-line-2"
                          control={control}
                          name={`personalization.${index}.messageLineTwo`}
                          maxLength={parseInt(maximumMessageLength)}
                          onKeyDown={nextFocus}
                          data-testid="message2"
                        />
                        <MessageLine
                          data-cy="message-line-3"
                          control={control}
                          name={`personalization.${index}.messageLineThree`}
                          maxLength={parseInt(maximumMessageLength)}
                          onKeyDown={nextFocus}
                          data-testid="message3"
                        />
                        <MessageLine
                          data-cy="message-line-4"
                          control={control}
                          name={`personalization.${index}.messageLineFour`}
                          maxLength={parseInt(maximumMessageLength)}
                          onKeyDown={nextFocus}
                          data-testid="message4"
                        />
                        <MessageLine
                          data-cy="message-line-5"
                          control={control}
                          name={`personalization.${index}.messageLineFive`}
                          maxLength={parseInt(maximumMessageLength)}
                          onKeyDown={nextFocus}
                          data-testid="message5"
                        />
                      </MessageInputs>
                      <HelperText data-cy="remaining-characters-text">
                        {remainingCharacters({
                          messageLineOne: values[index]?.messageLineOne,
                          messageLineTwo: values[index]?.messageLineTwo,
                          messageLineThree: values[index]?.messageLineThree,
                          messageLineFour: values[index]?.messageLineFour,
                          messageLineFive: values[index]?.messageLineFive,
                        })}{' '}
                        {t('personalization.characterCountLabel')}
                      </HelperText>
                    </MessageWrapper>
                  </Fields>
                  <Image>
                    {images?.length ? <ImagePreview images={[images[0]]} altLabel={name} /> : null}
                  </Image>
                </FieldsWrapper>

                <Preview
                  data-cy="preview"
                  index={index}
                  type={isVoucher(item) ? 'voucher' : 'event'}
                  itemId={id as number}
                  optionId={option?.id}
                  printablePersonalization={values[index] as Personalization}
                  propertyId={propertyId}
                  hasDefaultEVoucher={hasDefaultEVoucher}
                  hasDefaultOverprint={hasDefaultOverprint}
                  hasDefaultETicket={hasDefaultETicket}
                  hasDefaultTicketOverprint={hasDefaultTicketOverprint}
                />

                {isVoucher(item) && (
                  <ChangeDate data-cy="voucher-change-date">
                    <Text data-cy="voucher-change-date-text" isSmall isSecondary>
                      {t('personalization.changeDateText')}
                    </Text>
                    <SelectDateWrapper>
                      <DatePicker
                        data-cy="voucher-change-date-picker"
                        control={control}
                        name={`personalization.${index}.validFrom`}
                        label={t('personalization.validFrom')}
                      />
                    </SelectDateWrapper>
                  </ChangeDate>
                )}
              </FormRowWrapper>
            </div>
          ))}
          <BasketCTAButton data-cy="basket-cta-button">
            <Button data-cy="basket-submit-button" isPrimary type="submit" testId="form-submit">
              {t('personalization.update')}
            </Button>
          </BasketCTAButton>
          <BasketToolbar data-cy="basket-toolbar">
            <StickyBarWrapper id={id as string} data-cy="sticky-bar-wrapper">
              <StickyBarContent
                data-cy="sticky-bar-content"
                subtitle={name}
                name={option?.name}
                displayedPrice={option?.price || item.price}
                discountedPrice={option?.discount || item.discount}
                button={
                  <Button
                    data-cy="basket-sticky-submit-button"
                    isPrimary
                    type="submit"
                    testId="form-submit"
                  >
                    {t('personalization.update')}
                  </Button>
                }
              />
            </StickyBarWrapper>
          </BasketToolbar>
        </FormWrapper>
      </form>
    </FormProvider>
  );
}
